import { Component, ChangeDetectionStrategy, ElementRef, OnDestroy, OnInit, inject, input, output } from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

/**
 * "Enable editing as {group}?" (F9 write mode, story #3362) — the one confirmation before a
 * platform admin's viewpoint starts writing for real. Opened from the shell banner's "Enable
 * editing" (`ViewAsService.requestWrite`), rendered by the shell while `writeConfirmPending` is
 * on. States the three consequences: every change is written exactly as the group's manager
 * would write it, it is attributed to the admin's own email, and it is audited with its content.
 * Disabling editing later needs no confirmation (the safe direction).
 *
 * a11y (pages/forms.md): `role="alertdialog"` on the global `.dialog-card` (a high-impact
 * choice), focus trapped, Cancel takes the initial focus (the non-committal default), Escape /
 * backdrop / Cancel emit `closed`, the primary CTA carries the verb on a red fill (the same red
 * the banner turns once editing is on), and focus returns to whatever opened it — the banner
 * button — when the dialog leaves the DOM.
 */
@Component({
  selector: 'ext-view-as-write-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [A11yModule, MatButtonModule, MatIconModule, TranslateModule],
  template: `
  <div class="dialog-overlay" (click)="onOverlayClick($event)">
    <div class="dialog-card" role="alertdialog" aria-modal="true" aria-labelledby="vawd-title" aria-describedby="vawd-body"
         cdkTrapFocus [cdkTrapFocusAutoCapture]="true" (keydown)="onKeydown($event)" (click)="$event.stopPropagation()">
      <div class="vawd-head">
        <span class="vawd-icon" aria-hidden="true"><mat-icon>edit</mat-icon></span>
        <h2 class="dialog-title" id="vawd-title">{{ 'topbar.viewAs.confirmWrite.title' | translate: { name: groupName() } }}</h2>
      </div>
      <p class="dialog-body" id="vawd-body">{{ 'topbar.viewAs.confirmWrite.body' | translate: { name: groupName() } }}</p>
      <ul class="vawd-list dialog-body">
        <li><mat-icon aria-hidden="true">person</mat-icon><span>{{ 'topbar.viewAs.confirmWrite.attributed' | translate: { email: adminEmail() } }}</span></li>
        <li><mat-icon aria-hidden="true">history</mat-icon><span>{{ 'topbar.viewAs.confirmWrite.audited' | translate }}</span></li>
        <li><mat-icon aria-hidden="true">edit_off</mat-icon><span>{{ 'topbar.viewAs.confirmWrite.disableHint' | translate }}</span></li>
      </ul>
      <div class="dialog-actions">
        <button mat-stroked-button type="button" cdkFocusInitial (click)="closed.emit()">{{ 'common.cancel' | translate }}</button>
        <button mat-raised-button class="btn-danger" type="button" (click)="confirmed.emit()">
          <mat-icon aria-hidden="true">edit</mat-icon> {{ 'topbar.viewAs.confirmWrite.confirm' | translate }}
        </button>
      </div>
    </div>
  </div>`,
  styles: [`
    .vawd-head { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
    .vawd-head .dialog-title { margin: 0; }
    .vawd-icon {
      display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; width: 40px; height: 40px;
      border-radius: var(--ext-radius-md); background: var(--ext-danger-50); color: var(--ext-danger-700);
      mat-icon { font-size: 22px; width: 22px; height: 22px; }
    }
    .vawd-list {
      list-style: none; margin: 12px 0 0; padding: 10px 12px; display: flex; flex-direction: column; gap: 8px;
      border-radius: var(--ext-radius-md); background: var(--ext-surface-sunken); border: 1px solid var(--ext-border);
      font-size: 12.5px;
      li { display: flex; align-items: flex-start; gap: 8px; }
      mat-icon { flex-shrink: 0; font-size: 16px; width: 16px; height: 16px; color: var(--ext-navy-600); margin-top: 2px; }
    }
    .dialog-actions button mat-icon { margin-inline-end: 4px; }
    @media (max-width: 860px) { .dialog-actions button { min-height: 44px; } }
  `],
})
export class ViewAsWriteDialogComponent implements OnInit, OnDestroy {
  /** The viewed group's display name (the BE's `viewAsGroupName`, else the pinned name). */
  groupName = input.required<string>();
  /** The admin's own email — what every write will be recorded under. */
  adminEmail = input<string>('');
  confirmed = output<void>();
  closed = output<void>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private trigger: HTMLElement | null = null;

  ngOnInit(): void {
    this.trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }

  ngOnDestroy(): void {
    // Next macrotask: the dialog is gone and the banner has re-rendered — never leave focus on body.
    const trigger = this.trigger;
    this.trigger = null;
    if (trigger?.isConnected && !this.host.nativeElement.contains(trigger)) {
      setTimeout(() => { if (trigger.isConnected) trigger.focus(); }, 0);
    }
  }

  onKeydown(ev: KeyboardEvent): void {
    if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); this.closed.emit(); }
  }

  /** Only a click ON the backdrop closes the dialog (never one that bubbled out of the card). */
  onOverlayClick(ev: MouseEvent): void {
    if (ev.target === ev.currentTarget) this.closed.emit();
  }
}
